import { APIDeleteRequest, APIPutRequest } from "@punchup/utils/requests";
import {
  CacheEntities,
  fetchWithCache,
  generateCacheKey,
} from "@punchup/utils/caches";
import {
  SocialMediaLinkRow,
  SocialMediaProps,
  processSocialMediaLinkData,
} from "./socialMedia";
import { SpecialRow, getSpecialByID } from "./specials";
import createNoAuthSupabaseClient, {
  createNoAuthSupabaseClientWithRetry,
  getUser,
} from "../supabase/supabase";
import {
  knownNoisyPathsRegex,
  returnNotFoundAndLogWarning,
} from "@punchup/utils/vercel";
import { logException, logMessage } from "@punchup/sentry/sentry";

import { Database } from "../supabase.types";
import { ErrorWithAttachedObject } from "@punchup/utils/errors";
import { chunkArray } from "@punchup/utils/chunkArray";
import { SentryFeatureTags } from "@punchup/sentry/types";
import { PostgrestError, SupabaseClient } from "@supabase/supabase-js";
import dayjs from "dayjs";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { notFound } from "next/navigation";
import { validateComedian } from "./comedians-utils";

export const TestComedianIDs = [
  "a714614c-7747-4c66-aa30-ffa48840b6c6",
  "728db9b4-2005-4277-897b-0c19f7abcd95",
  "8b888959-6f7e-4316-8471-eb879e1d9d90",
  "a4ed537b-859a-44cb-a9c8-6d4167bf7f55",
  "05165de4-f63d-4aec-b845-b4c7d50ca126",
];

// Placeholder comedian names that should not be clickable or have profile pages.
// This approach is a holdover until we get a proper flag from the ticketing creation flag for
// placeholder shows. Once that's in place, we can remove this list and use the flag instead.
export const PLACEHOLDER_COMEDIAN_NAMES = [
  "Lineup To Be Announced Soon",
  "Lineup Coming Soon",
  "Secret Lineup",
  "TBA",
  "To Be Announced",
] as const;

// Helper function to check if a comedian name is a placeholder
export const isPlaceholderComedian = (comedianName: string): boolean =>
  (PLACEHOLDER_COMEDIAN_NAMES as readonly string[]).includes(comedianName);

// Helper function to check if a comedian should be treated as published/clickable
// A comedian is considered published if they are punchup verified, published_at <= today, and not in external preview mode
export const isComedianPublished = (comedian: {
  is_punchup_verified: boolean | null;
  published_at: string | null;
  is_external_preview: boolean | null;
}): boolean => {
  if (!comedian.is_punchup_verified || comedian.is_external_preview) {
    return false;
  }

  // Check if published_at exists and is less than or equal to today
  if (!comedian.published_at) {
    return false;
  }

  const publishedDate = dayjs(comedian.published_at);
  const today = dayjs();

  // Compare dates: published_at should be less than or equal to today
  return (
    publishedDate.isBefore(today, "day") || publishedDate.isSame(today, "day")
  );
};

/**
 * Returns true if a comedian's pages and content are accessible to regular users.
 *
 * This is distinct from isComedianPublished, which governs full public visibility
 * (feeds, search, sitemap). External Preview comedians have accessible pages and
 * content even without a published_at date — they are hidden from discovery only.
 *
 * Use this for content-access checks (e.g. page rendering, video token generation).
 * Use isComedianPublished for feed/search/sitemap visibility checks.
 */
export const isComedianAccessible = (comedian: {
  published_at: string | null;
  is_external_preview: boolean | null;
}): boolean => {
  if (comedian.is_external_preview) {
    return true;
  }
  if (!comedian.published_at) {
    return false;
  }
  const publishedDate = dayjs(comedian.published_at);
  const today = dayjs();
  return (
    publishedDate.isBefore(today, "day") || publishedDate.isSame(today, "day")
  );
};

export type ComedianRow = Database["public"]["Tables"]["comedians"]["Row"];
export type ComedianAdminRow = { admin_id: string; comedian_id: string };
export type ComedianAdminRowInsert =
  Database["public"]["Tables"]["comedian_dashboard_admins"]["Insert"];
export type ComedianInsert =
  Database["public"]["Tables"]["comedians"]["Insert"];
export type ComedianUpdate =
  Database["public"]["Tables"]["comedians"]["Update"];
export type Comedian = ComedianRow;

type UnprocessedComedian = ComedianRow & { specials?: SpecialRow[] } & {
  comedian_social_media_links?: SocialMediaLinkRow[];
};

export type ComedianWithSpecialsAndSocialMedia = {
  id: ComedianRow["id"];
  displayName: ComedianRow["display_name"];
  displayNameShort: ComedianRow["display_name_short"];
  slug: ComedianRow["slug"];
  publishedAtString: ComedianRow["published_at"];
  about: ComedianRow["about"];
  aboutImg: ComedianRow["about_img"];
  colors: {
    brandColor: string;
  };
  featuredSpecialID: ComedianRow["featured_special_id"];
  signatureImg: ComedianRow["signature_img"];
  socialMedia: SocialMediaProps[];
  specials: SpecialRow[];
  merchStoreLink: ComedianRow["merch_store_link"];
  shopifyMerchCollectionId: ComedianRow["shopify_merch_collection_id"];
  tipJarLink: ComedianRow["tip_jar_link"];
  isExternalPreview: ComedianRow["is_external_preview"];
  isPunchupVerified: ComedianRow["is_punchup_verified"];
};

export async function getComedianByID(
  comedian_id: string
): Promise<Comedian | null> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("comedians")
    .select("*")
    .eq("id", comedian_id)
    .maybeSingle();

  if (error) {
    console.error(error);
    logException(error, {
      extra: {
        id: comedian_id,
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.Comedian,
        comedianId: comedian_id,
      },
    });
  }
  return data;
}

/**
 * ### Get a comedian lite by it's ID
 * If the comedian is not found, this throws an error.
 * The function always returns a Comedian object, or execution stops, so it's safe
 * to assume that if the function returns, the comedian was found. This allows you
 * to avoid null checking the return value when it's reasonably safe to assume the
 * comedian exists.
 */
export async function getComedianByIDOrThrow(
  comedianId: string
): Promise<Comedian> {
  const comedian = await getComedianByID(comedianId);
  if (!comedian) {
    throw new ErrorWithAttachedObject(
      `getComedianByID: comedian not found`,
      comedianId,
      "comedianId"
    );
  }
  return comedian;
}

/**
 * A verified comedian by slug, following a comedian alias. `null` when neither
 * matches — logged to Sentry unless the slug is a known noisy path. Throws when
 * a query fails, so a transient error is retried rather than read as a miss.
 */
async function findComedianBySlug(slug: string): Promise<Comedian | null> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("comedians")
    .select("*")
    .eq("slug", slug.toLowerCase())
    .eq("is_punchup_verified", true)
    .maybeSingle();

  if (error) throw error;
  if (data) return data;

  // Log not found slug if it's not a known noisy path
  if (!knownNoisyPathsRegex.test(slug)) {
    // Last resort: look in aliases
    const { data: foundAlias, error: aliasError } = await supabase
      .from("aliases")
      .select("*")
      .eq("alias", slug)
      .maybeSingle();

    if (aliasError) throw aliasError;

    if (foundAlias) {
      const { type } = foundAlias;
      if (type === "comedian") {
        return findComedianBySlug(foundAlias.slug);
      }
    }

    logMessage("[getComedianBySlugOr404] slug not found", {
      level: "warning",
      tags: {
        feature: SentryFeatureTags.Comedian,
        comedianSlug: slug,
      },
    });
  }

  return null;
}

/**
 * Keeps the `Or404` contract when the lookup itself fails: the failure goes to
 * Sentry and the caller 404s, as it did before failures threw.
 */
const reportLookupFailure = (slug: string) => (error: unknown) => {
  logException(error, {
    extra: { slug },
    tags: { supabase: true, feature: SentryFeatureTags.Comedian },
  });
  return null;
};

export async function getComedianBySlugOr404(slug: string): Promise<Comedian> {
  const comedian = await findComedianBySlug(slug).catch(
    reportLookupFailure(slug)
  );
  if (comedian) return comedian;

  // Only call notFound() on the server
  if (typeof window === "undefined") {
    notFound();
  }

  // In theory, this should only fire on the front-end, because notFound() makes this function exist early on the server
  throw new ErrorWithAttachedObject(
    `Issue getting a single comedian (lite), for comedian: ${slug}`,
    slug,
    "comedianSlug"
  );
}

// Why have a Comedian type and a ComedianRow type?
// 1. Postgres downcases names, so snake_case is recommended over
//    CamelCase. By having both types and the process function below,
//    we explicitly map the Typescript attribut names to the Postgres
//    column names.
// 2. Supabase doesn't automatically convert all types, in particular
//    Postgres dates are typed as strings in Typescript. The process
//    function allows for explicit conversion of those types.

export function processComedianData(
  data: UnprocessedComedian
): ComedianWithSpecialsAndSocialMedia {
  return {
    id: data.id,
    // this is a datetime w/ a timezone in the database
    // but js Dates and dayjs objects can't be serialized,
    // so we keep it as a string on the interface.
    publishedAtString: data.published_at,
    about: data.about,
    aboutImg: data.about_img,
    // TODO: acutally figure out some way of handling colors
    // Question: should we just delete this for now? it's unused.
    colors: {
      brandColor: "bg-primary",
    },
    displayName: data.display_name,
    displayNameShort: data.display_name_short,
    featuredSpecialID: data.featured_special_id,
    signatureImg: data.signature_img,
    slug: data.slug,
    // temporary hack to keep social media working until it's in the db
    socialMedia: data.comedian_social_media_links
      ? data.comedian_social_media_links.map((socialMediaLink) =>
          processSocialMediaLinkData(socialMediaLink)
        )
      : [],
    specials: data.specials ?? [],
    merchStoreLink: data.merch_store_link,
    shopifyMerchCollectionId: data.shopify_merch_collection_id,
    tipJarLink: data.tip_jar_link,
    isExternalPreview: data.is_external_preview,
    isPunchupVerified: data.is_punchup_verified,
  };
}

/**
 * ### Get a comedian by it's slug
 * If the comedian is not found, this renders the 404 Not Found page.
 * The function always returns a Comedian object, or execution stops, so it's safe
 * to assume that if the function returns, the comedian was found.
 *
 * ! FOR SERVER USE ONLY !
 *
 * Do not use this function from client code. Use `getComedian` instead.
 */
export async function deprecated_getComedianWithSpecialsAndSocialMediaOr404(
  comedianSlug: string
): Promise<ComedianWithSpecialsAndSocialMedia> {
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    throw new Error(
      "getComedianWithSpecialsAndSocialMediaOr404 used in client code. Please fix this."
    );
  }
  const comedian =
    await deprecated_getComedianWithSpecialsAndSocialMedia(comedianSlug);
  if (!comedian) {
    returnNotFoundAndLogWarning("Comedian[slug] " + comedianSlug);
  }
  if (comedian.isPunchupVerified === false) {
    returnNotFoundAndLogWarning("Ghost Comedian[slug] " + comedianSlug);
  }
  return comedian;
}

/**
 * ### Get a comedian by it's slug
 * If the comedian is not found, this returns null.
 */
export async function deprecated_getComedianWithSpecialsAndSocialMedia(
  comedianSlug: string
): Promise<ComedianWithSpecialsAndSocialMedia | null> {
  const comedianSlugLowerCase = comedianSlug.toLowerCase();
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("comedians")
    .select(
      `
    *,
    specials!specials_comedian_id_fkey (*),
    comedian_social_media_links (*)
  `
    )
    .eq("slug", comedianSlugLowerCase)
    .maybeSingle();

  if (error) {
    logException(error);
  }
  if (!data) {
    return null;
  }

  return processComedianData(data);
}

export async function getComedianSocialMedia(
  comedianId: string
): Promise<SocialMediaProps[]> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("comedian_social_media_links")
    .select("*")
    .eq("comedian_id", comedianId);

  if (error) {
    logException(error);
  }

  return (
    data?.map((socialMediaLink) =>
      processSocialMediaLinkData(socialMediaLink)
    ) || []
  );
}

export async function getComediansByPublishedDate(
  published_at: string
): Promise<Comedian[]> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("comedians")
    .select("*")
    .order("display_name", { ascending: true })
    .gt("published_at", published_at);

  if (error) {
    console.error(error);
  } else if (data) {
    return data.filter((comedian) => !comedian.is_external_preview);
  }
  throw new Error("Issue getting comedians by published date");
}

/**
 * ### Get a comedian by it's ID
 * If the comedian is not found, this renders the 404 Not Found page.
 * The function always returns a Comedian object, or execution stops, so it's safe
 * to assume that if the function returns, the comedian was found. This allows you
 * to avoid null checking the return value when it's reasonably safe to assume the
 * comedian exists.
 *
 * ! FOR SERVER USE ONLY !
 *
 * Do not use this function from client code. Use `getComedian` instead.
 */
export async function deprecated_getComedianWithSpecialsAndSocialMediaByIDOr404(
  comedianId: string,
  includeUnverified?: boolean
): Promise<ComedianWithSpecialsAndSocialMedia> {
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    throw new Error(
      "getComedianWithSpecialsAndSocialMediaByIDOr404 used in client code. Please fix this."
    );
  }
  const comedian =
    await deprecated_getComedianWithSpecialsAndSocialMediaByID(comedianId);
  if (!comedian) {
    returnNotFoundAndLogWarning("Comedian[id] " + comedianId);
  }
  if (comedian.isPunchupVerified === false && !includeUnverified) {
    returnNotFoundAndLogWarning("Ghost Comedian[id] " + comedianId);
  }
  return comedian;
}

// Wrapper around getComedianWithSpecialsAndSocialMediaByIDOrThrow that caches the result for 5 minutes
export async function deprecated_getComedianWithSpecialsAndSocialMediaWithCache(
  comedianId: string
): Promise<ComedianWithSpecialsAndSocialMedia> {
  const cacheKey = generateCacheKey(CacheEntities.COMEDIAN, comedianId);
  const cachedComedian = await fetchWithCache(
    cacheKey,
    () =>
      deprecated_getComedianWithSpecialsAndSocialMediaByIDOrThrow(comedianId),
    300 // 5 minutes
  );
  return cachedComedian;
}

// Wrapper around getComedianByIDOrThrow that caches the result for 5 minutes
export async function getComedianByIDOrThrowWithCache(
  comedianId: string
): Promise<Comedian> {
  const cacheKey = generateCacheKey(CacheEntities.COMEDIAN, comedianId);
  const cachedComedian = await fetchWithCache(
    cacheKey,
    () => getComedianByIDOrThrow(comedianId),
    300 // 5 minutes
  );
  return cachedComedian;
}

/**
 * ### Batch-fetch comedians by ID in a single query (per chunk)
 *
 * Replaces N individual `getComedianByID` round trips with one
 * `.in("id", ids)` query per chunk. Use this instead of mapping
 * `getComedianByIDOrThrowWithCache` over a list: a user who manages/follows
 * hundreds of comedians would otherwise fan out hundreds of simultaneous
 * queries and time the caller out.
 *
 * Unlike `getComedianByIDOrThrow`, a missing ID is simply absent from the
 * result rather than a throw — a dangling reference (e.g. a deleted comedian
 * still referenced by a follow) degrades to "not in the map" instead of
 * crashing the caller. Duplicate IDs are de-duped; order is not guaranteed, so
 * callers that need lookup-by-id should build a `Map`.
 */
export async function getComediansByIDs(
  comedianIds: string[]
): Promise<Comedian[]> {
  const uniqueIds = Array.from(new Set(comedianIds));
  if (uniqueIds.length === 0) {
    return [];
  }

  const supabase = createNoAuthSupabaseClient();
  // Chunk to stay under PostgREST's URL/`IN`-list limits for large managers.
  const chunks = chunkArray(uniqueIds, 200);

  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const { data, error } = await supabase
        .from("comedians")
        .select("*")
        .in("id", chunk);

      if (error) {
        logException(error, {
          extra: { comedianIds: chunk },
          tags: {
            supabase: true,
            feature: SentryFeatureTags.Comedian,
          },
        });
        throw new ErrorWithAttachedObject(
          "getComediansByIDs: failed to fetch comedians",
          chunk.join(","),
          "comedianIds"
        );
      }

      return data ?? [];
    })
  );

  return results.flat();
}

/**
 * ### Get a comedian by it's ID
 * If the comedian is not found, this throws an error.
 * The function always returns a Comedian object, or execution stops, so it's safe
 * to assume that if the function returns, the comedian was found. This allows you
 * to avoid null checking the return value when it's reasonably safe to assume the
 * comedian exists.
 */
export async function deprecated_getComedianWithSpecialsAndSocialMediaByIDOrThrow(
  comedianId: string
): Promise<ComedianWithSpecialsAndSocialMedia> {
  const comedian =
    await deprecated_getComedianWithSpecialsAndSocialMediaByID(comedianId);
  if (!comedian) {
    throw new ErrorWithAttachedObject(
      `Comedian not found in comedians table.`,
      comedianId,
      "comedianId"
    );
  }
  return comedian;
}

/**
 * ### Get a comedian by it's slug
 * If the comedian is not found, this returns null.
 */
export async function deprecated_getComedianWithSpecialsAndSocialMediaByID(
  comedianId: string
): Promise<ComedianWithSpecialsAndSocialMedia | null> {
  const supabase = createNoAuthSupabaseClientWithRetry();
  const { status, data, error } = await supabase
    .from("comedians")
    .select(
      `
    *,
    specials!specials_comedian_id_fkey (*),
    comedian_social_media_links (*)
  `
    )
    .eq("id", comedianId)
    .limit(1)
    .maybeSingle();

  if (error) {
    logException(error, {
      extra: {
        id: comedianId,
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.Comedian,
        comedianId,
        status,
      },
    });
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return processComedianData(data);
    }
  }
  return null;
}

/** Throws on a failed lookup, so callers never read it as "no admins". */
export async function getComedianAdmins(
  supabase: SupabaseClient<Database>,
  comedianId: string
) {
  const { data, error } = await supabase
    .from("comedian_dashboard_admins")
    .select("admin_id")
    .eq("comedian_id", comedianId);

  if (error) {
    logException(error, {
      extra: {
        id: comedianId,
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.Comedian,
        comedianId,
      },
    });
    throw error;
  }
  return (data || []).map((admin) => admin.admin_id);
}

export async function getComedianSlugs(): Promise<string[]> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("comedians")
    .select("slug")
    .eq("is_punchup_verified", true);

  if (error) {
    console.error(error);
  } else if (data) {
    return data.map((comedianRow) => comedianRow.slug);
  }
  throw new Error("Issue getting all comedian slugs.");
}

export async function getPublishedComedians(): Promise<Comedian[]> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("comedians")
    .select("*")
    .lte("published_at", dayjs().toISOString())
    .eq("is_punchup_verified", true)
    .order("published_at", { ascending: false });

  if (error) {
    console.error(error);
    logException(error, {
      extra: {
        data: { error },
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.Comedian,
      },
    });
  } else if (data) {
    return data.filter((comedian) => !comedian.is_external_preview);
  }
  throw new Error("Issue getting published comedians.");
}

export async function getComediansWithSpecialsAndSocialMedia(): Promise<
  ComedianWithSpecialsAndSocialMedia[]
> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase.from("comedians").select(`
      *,
      specials!specials_comedian_id_fkey (*),
      comedian_social_media_links (*)
    `);

  if (error) {
    console.error(error);
  } else if (data) {
    const comedians = data.map((comedianRow) =>
      processComedianData(comedianRow)
    );
    return comedians;
  }
  throw new Error("Issue getting all comedians.");
}

export async function getComedians(
  includeUnverified?: boolean // set this to true if you want to include unverified comedians
): Promise<Comedian[]> {
  const supabase = createNoAuthSupabaseClient();
  const query = supabase
    .from("comedians")
    .select("*")
    .order("display_name", { ascending: true });

  // Only filter for verified comedians if includeUnverified is not true
  if (!includeUnverified) {
    query.eq("is_punchup_verified", true);
  }

  const { data, error } = await query;

  if (error) {
    console.error(error);
  } else if (data) {
    return data;
  }
  throw new Error("Issue getting all comedians (lite).");
}

// Cached for 5 minutes. A failed query is retried by fetchWithCache; a slug
// that simply matches nothing 404s at once, because notFound() is called
// outside the cache — thrown inside it, the 404 was retried for ~14s.
export async function getComedianBySlugWithCacheOr404(
  slug: string
): Promise<Comedian> {
  const cacheKey = generateCacheKey(CacheEntities.COMEDIAN, slug);
  const cachedComedian = await fetchWithCache(
    cacheKey,
    () => findComedianBySlug(slug),
    300 // 5 minutes
  ).catch(reportLookupFailure(slug));
  if (!cachedComedian) notFound();
  return cachedComedian;
}

export async function createOrUpdateComedian(
  supabase: SupabaseClient<Database>,
  comedian: ComedianInsert,
  admins: ComedianAdminRowInsert[]
) {
  const user = await getUser(supabase);
  if (!user) {
    throw new Error(
      "Supabase client must have an active session to create/update a comedian."
    );
  }

  // Validate comedian
  const validationError = await validateComedian(comedian);
  if (validationError) {
    return validationError;
  }

  const { count, error } = await supabase.from("comedians").upsert(comedian, {
    onConflict: "id",
    ignoreDuplicates: false, // We set this false in order to update all fields for an existing comedian
    count: "exact",
  });

  if (error) {
    console.error(error);
    logException(error, {
      extra: {
        comedian: comedian.display_name,
      },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.Comedian,
        comedianId: comedian.id,
      },
    });
    return { error: error };
  }

  // A successful upsert affects exactly one row. We key success off the affected
  // row count rather than the HTTP status, because Supabase/PostgREST returns
  // 201 when it inserts a new row and 200 when it updates an existing one (and
  // the exact code varies by version) — a status check would wrongly reject
  // edits as failures.
  if (count !== 1) {
    // No error but an unexpected row count: log in Sentry and fail loudly.
    throw new ErrorWithAttachedObject(
      `Unexpected row count create/update different than 1 for ${comedian.display_name} creation/update`,
      comedian.display_name,
      "comedianName"
    );
  }

  // comedian update/creation was successful, now update admins
  // update comedian_dashboard_admins table if they are not already in the table
  if (comedian.id !== undefined) {
    const { data: adminsData, error: adminsError } = await supabase
      .from("comedian_dashboard_admins")
      .select("admin_id")
      .eq("comedian_id", comedian.id);

    if (adminsError) {
      return { error: adminsError };
    }

    const existingAdminIDs = (adminsData || []).map((admin) => admin.admin_id);
    const adminsToInsert = admins.filter(
      (admin) => !existingAdminIDs.includes(admin.admin_id)
    );
    const adminsToDelete = existingAdminIDs.filter(
      (adminID) => !admins.map((admin) => admin.admin_id).includes(adminID)
    );

    // deletions
    if (adminsToDelete.length > 0) {
      const deleteErrors = await Promise.all(
        adminsToDelete.map(async (adminId) => {
          return APIDeleteRequest(
            `/api/v2/internal/users/${adminId}/roles/comedian-admin`,
            {
              comedianId: comedian.id,
            }
          )
            .catch((e) => {
              return e;
            })
            .then(() => null);
        })
      );

      if (deleteErrors.some((e) => e != null)) {
        return { error: new Error("Error deleting comedian admins") };
      }
    }

    // inserts
    if (adminsToInsert.length > 0) {
      const insertErrors = await Promise.all(
        adminsToInsert.map(async (admin) => {
          return APIPutRequest(
            `/api/v2/internal/users/${admin.admin_id}/roles/comedian-admin`,
            {
              comedianId: comedian.id,
            }
          )
            .catch((e) => {
              return e;
            })
            .then(() => null);
        })
      );

      if (insertErrors.some((e) => e != null)) {
        return { error: new Error("Error inserting comedian admins") };
      }
    }
  }
  return { error: null };
}

export async function updateFeaturedSpecial(
  supabase: SupabaseClient<Database>,
  comedian: Pick<ComedianUpdate, "id" | "featured_special_id">
) {
  const user = await getUser(supabase);
  if (!user) {
    throw new Error(
      "Supabase client must have an active session to create/update a comedian."
    );
  }

  const { id, featured_special_id } = comedian;

  if (!id) {
    throw new Error(
      "Cannot continue with update because you did not pass an ID for this comedian."
    );
  }

  // If a featured special ID is passed, validate that it actually exists
  if (featured_special_id) {
    try {
      const featuredSpecial: SpecialRow | null = featured_special_id
        ? await getSpecialByID(featured_special_id)
        : null;
      if (!featuredSpecial) {
        // This code is dead code for now, but I feel like we should leave it in case the getSpecialByID()
        // function changes an stops throwing when a special is not found, and instead returns null
        throw new Error(
          "An existing Special was not found for the UUID provided. Cannot continue with the comedian update"
        );
      }
    } catch (error) {
      console.error(error);
      return { error };
    }
  }

  const { count, status, error } = await supabase
    .from("comedians")
    .update(
      {
        featured_special_id,
      } as ComedianUpdate,
      { count: "exact" }
    )
    .eq("id", id);

  if (error) {
    console.error(error);
    return { error: error };
  } else if (status === 204) {
    if (count === 1) {
      return { error: null };
    } else {
      // Log in Sentry that something happened that caused row count update to be not 1
      const error = `Error while setting featured special id for comedian with id ${comedian.id}. Row count was different than 1.`;
      console.error(error);
      return { error };
    }
  } else {
    // Log in Sentry that something happened that caused row count update to be not 1
    const error = `Error while setting featured special id for comedian with id ${comedian.id}. Status: ${status}.`;
    console.error(error);
    return { error };
  }
}

// Decide which image to use in priority order: special, comedian, PunchUp logo
/** Preview image for a comedian with no `about_img`. */
export const COMEDIAN_FALLBACK_METADATA_IMAGE_URL =
  "https://imagedelivery.net/7Ze32-hXUdrEDVtqvlbDMQ/dc9b5063-beaf-4709-e04b-eca128e74c00/public";

export const getComedianMetadataImageUrl = async (
  comedian: Comedian,
  special?: SpecialRow
) => {
  return special?.preview_img
    ? getCDNImageURL(special.preview_img)
    : comedian.about_img
      ? getCDNImageURL(comedian.about_img)
      : COMEDIAN_FALLBACK_METADATA_IMAGE_URL;
};

// copying the spec of getPublishedComedians, returning Comedian[]
export const getPublishedComediansCache = async (): Promise<Comedian[]> => {
  const supabase = createNoAuthSupabaseClient();
  const { data } = await supabase
    .from("comedians")
    .select("*")
    .eq("is_external_preview", false)
    .eq("is_punchup_verified", true)
    .lte("published_at", dayjs().toISOString());
  if (data) {
    return data;
  }
  return [];
};

export function isTestComedian(comedianId: string) {
  return TestComedianIDs.includes(comedianId);
}

export async function getArtistPreferences(comedianId: string) {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("artist_preferences")
    .select("*")
    .eq("artist_id", comedianId);

  if (error) {
    console.error(error);
  }

  return data;
}

// Given a list of Comedian elements, retrieves the profile image url for each
// Fallback to Punchup logo if a comedian's about_image is missing
export const getComediansImageMap = async (
  comedians: (Comedian | null)[]
): Promise<
  Array<
    Pick<ComedianWithSpecialsAndSocialMedia, "id" | "slug" | "displayName"> & {
      imgUrl: string;
    }
  >
> => {
  const comedianMaps = await Promise.all(
    comedians.map(async (comedian) => {
      if (!comedian) {
        return null;
      }
      const { id, display_name, slug, about_img } = comedian;
      let comedianImgUrl =
        "https://imagedelivery.net/7Ze32-hXUdrEDVtqvlbDMQ/dc9b5063-beaf-4709-e04b-eca128e74c00/public";
      if (about_img) {
        comedianImgUrl = getCDNImageURL(about_img);
      }
      return {
        imgUrl: comedianImgUrl,
        id,
        displayName: display_name,
        slug,
      };
    })
  );

  // Filter out null values
  return comedianMaps.filter(
    (
      comedianMap
    ): comedianMap is Pick<
      ComedianWithSpecialsAndSocialMedia,
      "id" | "slug" | "displayName"
    > & {
      imgUrl: string;
    } => comedianMap !== null
  );
};

// Given a list of comedian objects return the comedian display_name when passing a comedian Id
export const getComedianDisplayName = (
  comedians: Comedian[],
  comedianId: string
) => {
  return comedians.filter((comedian) => comedian.id === comedianId)[0]
    .display_name;
};

// Given a list of comedian objects return the slug when passing a comedian Id
export const getComedianSlug = (comedians: Comedian[], comedianId: string) => {
  return comedians.filter((comedian) => comedian.id === comedianId)[0].slug;
};

export const checkSlugAvailability = async (
  slug: string
): Promise<"AVAILABLE" | "UNAVAILABLE" | "CLAIMABLE"> => {
  const response = await fetch(`/api/public/comedians/${slug}`);
  if (response.status === 404) {
    return "AVAILABLE";
  }

  if (response.status === 200) {
    const data = await response.json();
    if (!!data.is_punchup_verified) {
      return "UNAVAILABLE";
    }
    return "CLAIMABLE";
  }

  throw new Error("Failed to check comedian slug availability");
};

export type PageType = Database["public"]["Enums"]["page_types"];

// Human-readable labels for each page type, in the order they should appear in
// selection UIs (`comedian` first because it is the default for a new page).
// Typed as Record<PageType, string> so that adding a new value to the
// page_types enum is a compile error here until a label is supplied — keeping
// internal tooling in sync with the DB. This is the single source of truth for
// the set, the labels, AND the display order.
export const PAGE_TYPE_DISPLAY_NAMES: Record<PageType, string> = {
  comedian: "Comedian",
  musician: "Musician",
  podcast: "Podcast",
  magician: "Magician",
  medium: "Medium",
  journalist: "Journalist",
  film: "Film",
  production_company: "Production Company",
  venue_page: "Venue",
};

// The type assigned to a page when none has been explicitly chosen.
export const DEFAULT_PAGE_TYPE: PageType = "comedian";

// Ordered { value, label } pairs for rendering a page-type picker, derived from
// PAGE_TYPE_DISPLAY_NAMES (object key order) so there is exactly one list to
// maintain — a new enum value automatically appears once it has a label.
export const PAGE_TYPE_OPTIONS: { value: PageType; label: string }[] = (
  Object.keys(PAGE_TYPE_DISPLAY_NAMES) as PageType[]
).map((value) => ({
  value,
  label: PAGE_TYPE_DISPLAY_NAMES[value],
}));

// Narrows an arbitrary value to a known PageType — used to validate API input.
export function isPageType(value: unknown): value is PageType {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(PAGE_TYPE_DISPLAY_NAMES, value)
  );
}

export function getPageTypeDisplayName(pageType: string): string {
  return PAGE_TYPE_DISPLAY_NAMES[pageType as PageType] ?? "Comedian";
}

export async function getPageType(pageId: string): Promise<PageType> {
  const supabase = createNoAuthSupabaseClient();
  const { data } = await supabase
    .from("page_type")
    .select("type")
    .eq("page_id", pageId)
    .maybeSingle();

  return data?.type ?? DEFAULT_PAGE_TYPE;
}

export async function getPageTypesForComedians(
  pageIds: string[]
): Promise<Record<string, string>> {
  if (!pageIds.length) return {};
  const supabase = createNoAuthSupabaseClient();
  const { data } = await supabase
    .from("page_type")
    .select("page_id, type")
    .in("page_id", pageIds);

  const result: Record<string, string> = {};
  for (const row of data ?? []) {
    result[row.page_id] = row.type;
  }
  return result;
}

/**
 * Set the page type for a page (a comedians row).
 *
 * A page is modelled as having a single type, but the page_type table's unique
 * constraint is on (page_id, type) — so we first remove any other type rows for
 * the page, then idempotently ensure the desired type row exists.
 *
 * Writing to page_type requires the service role (only `select` is granted to
 * authenticated/anon), so callers MUST pass a service-role Supabase client.
 * This is intended to be called from a server-side admin API route, not the
 * browser.
 */
export async function setPageTypeForPage(
  supabase: SupabaseClient<Database>,
  pageId: string,
  type: PageType
): Promise<{ error: PostgrestError | null }> {
  // Remove any previously-assigned type(s) that differ from the new one.
  const { error: deleteError } = await supabase
    .from("page_type")
    .delete()
    .eq("page_id", pageId)
    .neq("type", type);

  if (deleteError) {
    logException(deleteError, {
      extra: { pageId, type },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.Comedian,
        comedianId: pageId,
      },
    });
    return { error: deleteError };
  }

  // Ensure the desired type row exists. ignoreDuplicates makes this a no-op
  // when the page already has the requested type.
  const { error: upsertError } = await supabase.from("page_type").upsert(
    { page_id: pageId, type },
    {
      onConflict: "page_id,type",
      ignoreDuplicates: true,
    }
  );

  if (upsertError) {
    logException(upsertError, {
      extra: { pageId, type },
      tags: {
        supabase: true,
        feature: SentryFeatureTags.Comedian,
        comedianId: pageId,
      },
    });
  }

  return { error: upsertError };
}
